import { Archive, HandCoins, Pencil, SkipForward, Wallet } from "lucide-react";
import type { Recurring } from "../../types";
import { dateLabel, money } from "../../lib/finance";
import { categoryIcon } from "../../components/CategoryIcon";
import { Private } from "../../components/Privacy";

const FREQUENCY_LABEL: Record<string, string> = { weekly: "Weekly", monthly: "Monthly", yearly: "Yearly" };

function dueStatus(bill: Recurring, today: string): { label: string; tone: "overdue" | "soon" | "later" | "paused" } {
  if (!bill.active) return { label: "Paused", tone: "paused" };
  const income = bill.type === "Income";
  const days = Math.round((Date.parse(`${bill.nextDue.slice(0, 10)}T12:00:00Z`) - Date.parse(`${today}T12:00:00Z`)) / 86400000);
  if (days < 0) return { label: `${income ? "Late" : "Overdue"} ${-days}d`, tone: "overdue" };
  if (days === 0) return { label: income ? "Expected today" : "Due today", tone: "soon" };
  if (days <= 7) return { label: `In ${days}d`, tone: "soon" };
  return { label: `In ${days}d`, tone: "later" };
}

/** One bill or regular income on the Recurring screen: when it is next due, and what to do with it. */
export default function BillCard({
  bill,
  today,
  busy,
  onOpen,
  onPay,
  onSkip,
  onEdit,
  onArchive,
}: {
  bill: Recurring;
  today: string;
  busy: boolean;
  onOpen: () => void;
  onPay: () => void;
  onSkip: () => void;
  onEdit: () => void;
  onArchive: () => void;
}) {
  const pending = bill.id.startsWith("local:bill:");
  const due = dueStatus(bill, today);
  const Icon = categoryIcon(bill.category || "");
  const isIncome = bill.type === "Income";
  const payLabel = isIncome ? "Receive" : "Pay";
  const freqLabel = FREQUENCY_LABEL[bill.frequency.toLowerCase()] || bill.frequency;

  return (
    <article
      className={`recurring-card${bill.active ? "" : " is-paused"}${
        due.tone === "overdue" ? " is-overdue" : due.tone === "soon" ? " is-soon" : ""
      }`}
    >
      <div
        className="recurring-card-main"
        role="button"
        tabIndex={0}
        onClick={onOpen}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onOpen();
          }
        }}
        aria-label={`Open details for ${bill.name}`}
      >
        <div className="recurring-card-identity">
          <span className={`recurring-icon-box ${due.tone}`}>
            <Icon size={18} />
          </span>
          <div className="recurring-card-copy">
            <div className="recurring-card-title-line">
              <strong className="recurring-card-name">{bill.name}</strong>
              {pending && <span className="recurring-pending-badge">Unsynced</span>}
            </div>
            <div className="recurring-card-meta">
              <span>{freqLabel}</span>
              {bill.active && bill.nextDue && (
                <>
                  <span className="dot">·</span>
                  <span>Due {dateLabel(bill.nextDue)}</span>
                </>
              )}
              {bill.category && (
                <>
                  <span className="dot">·</span>
                  <span className="recurring-category-tag">{bill.category}</span>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="recurring-card-right">
          <div className="recurring-card-figure">
            {bill.amount > 0 ? (
              <strong className={`recurring-amount${isIncome ? " income" : ""}`}>
                <Private>{isIncome ? "+" : ""}{money(bill.amount, bill.currency)}</Private>
              </strong>
            ) : (
              <span className="recurring-amount-varies">Varies</span>
            )}
          </div>
          <span className={`recurring-status-badge ${pending ? "paused" : due.tone}`}>
            {pending ? "Not synced" : due.label}
          </span>
        </div>
      </div>

      <div className="recurring-card-actions" onClick={(e) => e.stopPropagation()}>
        <div className="recurring-primary-actions">
          {bill.active && !pending && (
            <>
              <button
                type="button"
                className="recurring-btn-pay"
                disabled={busy}
                onClick={onPay}
                aria-label={`${payLabel} ${bill.name}`}
              >
                {isIncome ? <HandCoins size={14} /> : <Wallet size={14} />}
                <span>{payLabel}</span>
              </button>
              <button
                type="button"
                className="recurring-btn-skip"
                disabled={busy}
                onClick={onSkip}
                aria-label={`Skip cycle for ${bill.name}`}
              >
                <SkipForward size={14} />
                <span>Skip</span>
              </button>
            </>
          )}
          {!bill.active && (
            <span className="recurring-paused-text">Paused subscription</span>
          )}
        </div>

        <div className="recurring-secondary-actions">
          <button
            type="button"
            className="recurring-icon-btn"
            onClick={onEdit}
            aria-label={`Edit ${bill.name}`}
            title="Edit"
          >
            <Pencil size={15} />
          </button>
          {bill.active && (
            <button
              type="button"
              className="recurring-icon-btn danger"
              disabled={busy}
              onClick={onArchive}
              aria-label={`Archive ${bill.name}`}
              title="Archive"
            >
              <Archive size={15} />
            </button>
          )}
        </div>
      </div>
    </article>
  );
}
